/**
 * @lines98/client/lib/auth-modal — поверхность входа = модалка (account-profile-
 * redesign.md §4, P3).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-05-30 «ствол → ветки» + spec §4.1):
 * вход открывается из ТРЁХ мест — (1) пилюля «Войти» в рейле (§2.1), (2) CTA гостевого
 * Профиля (§6.2), (3) re-login после ревока (§4.1). ОДИН модуль обслуживает все три
 * вызова → нет второй реализации формы входа, нет отдельной /login-страницы (research
 * анти-скоуп). Тело модалки = существующий `components/auth-panel` в ГОСТЕВОМ состоянии
 * (3 OAuth-кнопки + раскрываемая email-форма) — переиспользуется, НЕ копируется.
 *
 * SCOPE: `openAuthModal(opts)` — открыть модалку поверх текущего экрана (контекст под
 * ней цел, §4.3). Native `<dialog>` top-layer (паттерн `components/confirm-dialog`:
 * focus-trap, Esc, клик по фону = закрыть, a11y из коробки — НЕ выбирать стек/либу).
 * Успешный вход (`auth-panel.onAuthSuccess`) → модалка авто-закрывается + колбэк
 * `onAuthSuccess` наверх (рейл reconcile'ит account-slot, Профиль — мета-секции, §4.2).
 *
 * OAUTH LEAVE-GUARD (§4.4): OAuth-кнопки уводят на провайдера (full redirect) → сброс
 * страницы. Перед redirect нужен leave-guard, ЕСЛИ под модалкой запущена партия. Этот
 * guard ИНЪЕКТИРУЕТСЯ в auth-modal обёрткой над onGoogle/onDiscord (НЕ дублируется в
 * auth-panel). Email login/register НЕ делают redirect → guard им не нужен.
 *
 * NOT IN SCOPE: сама форма-логика входа/регистрации (живёт в auth-panel), сетевые
 * сервисы (oauth-login / local-auth — инъектируются вызывающим), RU-маппинг ошибок
 * (auth-panel). Один открытый за раз (singleton-реестр).
 *
 * Cross-references:
 *   - account-profile-redesign.md §4 (модалка), §4.1 (3 вызова), §4.2 (закрытие),
 *     §4.4 (OAuth-guard), AC-6/AC-7.
 *   - components/auth-panel (тело), components/confirm-dialog (паттерн dialog).
 *   - auth-modal.css (co-located; токен-размеры + layout-plan 4 класса).
 */

import '../components/dialog-plaque.css';
import './auth-modal.css';

import {
  mountAuthPanel,
  type AuthPanelHandle,
} from '../components/auth-panel.js';
import { createIconButton, ICON_CLOSE } from '../components/icon-button.js';
import { t } from './i18n/index.js';
import type { LoginResult, RegisterResult } from '../services/local-auth.js';

export interface AuthModalOptions {
  /**
   * Запустить Google OAuth (redirect). Вызывающий передаёт «сырой» старт (loginWithGoogle);
   * leave-guard перед redirect навешивает auth-modal (§4.4) — здесь его НЕ дублируем.
   */
  readonly onGoogle: () => void | Promise<void>;
  /** Запустить Discord OAuth (redirect) — аналогично (§4.4). */
  readonly onDiscord: () => void | Promise<void>;
  /**
   * Регистрация email-аккаунта (services/local-auth.register). Вариант B
   * (owner-decision 2026-06-21): БЕЗ ника — ник задаётся в Профиле.
   */
  readonly register: (args: {
    email: string;
    password: string;
  }) => Promise<RegisterResult>;
  /** Вход email-аккаунта (services/local-auth.login). */
  readonly login: (args: { email: string; password: string }) => Promise<LoginResult>;
  /**
   * Leave-guard перед OAuth-redirect (§4.4). Возвращает true → можно уходить (или
   * партии нет / игрок подтвердил). false → отмена redirect (остаёмся в модалке,
   * партия цела). Вызывается ТОЛЬКО для OAuth-кнопок (email не делает redirect).
   * Инъектируется вызывающим (app.ts — confirmAbandon + suppressNextUnloadGuard).
   */
  readonly guardOAuthRedirect: () => Promise<boolean>;
  /**
   * Успешный вход/регистрация (любой email-путь). Модалка УЖЕ закрылась; вызывающий
   * reconcile'ит account-slot (рейл) + Профиль (§4.2 / §2.3). `displayName` — ник
   * нового залогиненного (или null).
   */
  readonly onAuthSuccess: (displayName: string | null) => void;
}

export interface AuthModalHandle {
  /** Программно закрыть (напр. при reconcile извне). Идемпотентно. */
  close(): void;
}

// Один открытый за раз — повторный openAuthModal закрывает предыдущий.
let activeClose: (() => void) | null = null;

/**
 * Открыть модалку входа. Тело = auth-panel (гостевое). Возвращает handle (close).
 * Esc / клик по фону / ✕ закрывают без входа (контекст под модалкой цел). Успешный
 * вход авто-закрывает модалку + дёргает `onAuthSuccess`.
 */
export function openAuthModal(opts: AuthModalOptions): AuthModalHandle {
  // Закрыть ранее открытую (один за раз).
  activeClose?.();

  const dialog = document.createElement('dialog');
  // dialog-plaque — общий примитив-контейнер плашки (как у confirm-dialog/restore);
  // auth-modal — только внутренняя раскладка (шапка + тело).
  dialog.className = 'dialog-plaque auth-modal';
  dialog.setAttribute('aria-label', t('auth.panel.modalTitle'));

  // Шапка: заголовок «Авторизация» (по центру, шрифт кнопок) + ✕ (примитив icon-button).
  const header = document.createElement('header');
  header.className = 'auth-modal-header';
  const titleEl = document.createElement('h2');
  titleEl.className = 'auth-modal-title';
  titleEl.textContent = t('auth.panel.modalTitle');
  const closeBtn = createIconButton({
    glyph: ICON_CLOSE,
    label: t('auth.panel.modalClose'),
    variant: 'ghost',
    className: 'auth-modal-close',
  }).element;
  header.append(titleEl, closeBtn);
  dialog.appendChild(header);

  // Тело — host для auth-panel.
  const body = document.createElement('div');
  body.className = 'auth-modal-body';
  dialog.appendChild(body);

  document.body.appendChild(dialog);

  let unmounted = false;
  let panel: AuthPanelHandle | null = null;

  const teardown = (): void => {
    if (unmounted) return;
    unmounted = true;
    if (activeClose === close) activeClose = null;
    panel?.unmount();
    panel = null;
    closeBtn.removeEventListener('click', onCloseClick);
    dialog.removeEventListener('cancel', onCancel);
    dialog.removeEventListener('click', onBackdropClick);
    if (dialog.open) dialog.close();
    dialog.remove();
  };

  function close(): void {
    teardown();
  }

  // ── OAuth-кнопки: leave-guard ПЕРЕД redirect (§4.4). Email не трогаем (in-place). ──
  const wrapOAuth = (start: () => void | Promise<void>) => async (): Promise<void> => {
    if (!(await opts.guardOAuthRedirect())) return; // отмена → остаёмся в модалке.
    await start();
  };

  // Тело = auth-panel (гостевое). identity:null → панель рисует 3 кнопки + форму.
  panel = mountAuthPanel(body, {
    onGoogle: wrapOAuth(opts.onGoogle),
    onDiscord: wrapOAuth(opts.onDiscord),
    register: opts.register,
    login: opts.login,
    onAuthSuccess: (displayName) => {
      // Успех (email-путь): авто-закрыть модалку (§4.2) → колбэк наверх (reconcile).
      const cb = opts.onAuthSuccess;
      close();
      cb(displayName);
    },
  });

  const onCloseClick = (e: Event): void => {
    e.preventDefault();
    close();
  };
  closeBtn.addEventListener('click', onCloseClick);

  // Esc → native `cancel` (preventDefault чтобы наш teardown снял из DOM).
  const onCancel = (e: Event): void => {
    e.preventDefault();
    close();
  };
  dialog.addEventListener('cancel', onCancel);

  // Клик по ::backdrop (target === dialog) = закрыть (§4.2).
  const onBackdropClick = (e: MouseEvent): void => {
    if (e.target === dialog) close();
  };
  dialog.addEventListener('click', onBackdropClick);

  dialog.showModal();
  activeClose = close;

  return { close };
}
